import { notify } from '@affine/component';
import { ChatContextReadError } from '@affine/core/blocksuite/ai/components/ai-chat-content/chat-context-manager';
import type {
  ChatContextSelection,
  UiContextBinding,
} from '@affine/core/blocksuite/ai/components/ai-chat-content/type';
import { captureNativeContextSelection } from '@affine/core/blocksuite/ai/utils/extract';
import {
  getChatPanelPickingControl,
  registerChatPanelPickingSource,
} from '@affine/core/desktop/pages/workspace/layouts/chat-panel-host-registry';
import { I18n } from '@affine/i18n';
import { isInsideEdgelessEditor } from '@blocksuite/affine/shared/utils';
import { EditorHost } from '@blocksuite/affine/std';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type {
  WorkspaceContextIssue,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { nanoid } from 'nanoid';

import { pickMarkElement } from './context-pick-mark';
import {
  CONTEXT_PICK_HOVER_ATTRIBUTE,
  CONTEXT_PICKED_ATTRIBUTE,
  CONTEXT_PICKING_ROOT_ATTRIBUTE,
  CONTEXT_PICKING_TOGGLE_ATTRIBUTE,
} from './context-picking-attributes';
import {
  captureSemanticDomRef,
  projectSemanticPick,
  resolveSemanticPickTarget,
} from './semantic-dom-context';
import { mapWorkspaceContextIssueToMessageKey } from './workspace-context-messages';

// Form fields are pickable: a picked field is what Copilot fills in. Secret content is picked as
// the element that holds it, by its label only (the semantic admission decides that). What the
// user cannot see, the selector toggle and Copilot itself are never picked; Copilot's controls keep
// acting.
const PICK_EXCLUDED_PATH = `[hidden],[inert],[aria-hidden="true"],[${CONTEXT_PICKING_TOGGLE_ATTRIBUTE}],[data-copilot-ignore]`;

type PathHit = { element: Element; host: EditorHost | undefined };

type HandResult = { ok: true } | { ok: false; reason: WorkspaceContextIssue };

const issueOf = (error: unknown): WorkspaceContextIssue =>
  error instanceof ChatContextReadError ? error.reason : 'unavailable';

/** Every bound root, so a helper button finds the one it sits in. */
const handRoots = new Map<HTMLElement, (element: Element) => HandResult>();

/**
 * Hands `element` to Copilot as a pill of the bound root it sits in, the
 * same pill a pick with the selector gives.
 */
export function handToCopilot(element: Element): HandResult {
  for (
    let current: Node | null = element;
    current;
    current =
      current.parentNode ??
      (current instanceof ShadowRoot ? current.host : null)
  ) {
    const hand =
      current instanceof HTMLElement ? handRoots.get(current) : undefined;
    if (hand) return hand(element);
  }
  return { ok: false, reason: 'unavailable' };
}

/** A scoped hit adapter. The existing Content manager owns every admitted ref. */
export function bindContextPickingRoot(
  root: HTMLElement,
  options: {
    scope: WorkspaceViewScope;
    ownerId: string;
    sourceKind: 'main' | 'dialog';
    isActive(): boolean;
    includeDetailedObject?(): boolean;
    onIssue?(reason: WorkspaceContextIssue): void;
  }
) {
  const { scope } = options;
  const generation = nanoid(),
    sourceKey = `ui:${options.ownerId}`;
  const source = registerChatPanelPickingSource(scope, sourceKey, generation);
  const control = getChatPanelPickingControl(scope);
  // Native owner registrations only; selected IDs/bodies stay on Content/Store.
  type Source = ReturnType<typeof registerChatPanelPickingSource>;
  const nativeSources = new Map<string, Source>();
  // Marked elements of admitted picks; the selection itself stays on Content.
  const picked = new Map<
    Element,
    { key: string; isSelected: (key: string) => boolean }
  >();
  let disposed = false,
    listening = false,
    sequence = 0;
  let downTarget: EventTarget | null = null;
  let hovered: Element | null = null;
  const assertCurrent = () => {
    if (disposed || !root.isConnected || !options.isActive())
      throw new ChatContextReadError('stale');
  };
  const report = (error: unknown) => {
    const reason =
      error && typeof error === 'object' && 'reason' in error
        ? error.reason
        : 'unavailable';
    const issue =
      reason === 'stale' || reason === 'invalid' || reason === 'oversized'
        ? reason
        : 'unavailable';
    if (options.onIssue) options.onIssue(issue);
    else
      notify.error({
        title: I18n.t(mapWorkspaceContextIssueToMessageKey(issue)),
      });
  };
  // The mark sits on the box the user sees, which a picked wrapper hands to its content.
  const markPicked = (
    element: Element,
    key: string,
    isSelected: (key: string) => boolean
  ) => {
    const mark = pickMarkElement(element);
    picked.set(mark, { key, isSelected });
    mark.setAttribute(CONTEXT_PICKED_ATTRIBUTE, '');
  };
  // A click on what is already picked takes the pick back.
  const unpick = (from: Source, key: string) => {
    if (!from.isSelected(key)) return false;
    from.unpick(key);
    return true;
  };
  // A mark lives exactly as long as its chip on the Content manager.
  const reconcilePicked = () => {
    for (const [element, mark] of picked)
      if (disposed || !mark.isSelected(mark.key)) {
        picked.delete(element);
        element.removeAttribute(CONTEXT_PICKED_ATTRIBUTE);
      }
  };
  const setHovered = (target: Element | null) => {
    const element = target && pickMarkElement(target);
    if (element === hovered) return;
    hovered?.removeAttribute(CONTEXT_PICK_HOVER_ATTRIBUTE);
    hovered = element;
    hovered?.setAttribute(CONTEXT_PICK_HOVER_ATTRIBUTE, '');
  };
  // Only real user pointers reach picking. Copilot's own activations
  // (navigation `element.click()` and every other synthetic event) are never
  // trusted, so they pass through to the UI they target.
  const userHit = (event: MouseEvent) =>
    event.isTrusted &&
    control.isEnabled() &&
    !event.defaultPrevented &&
    options.isActive();
  const pathHit = (event: MouseEvent): PathHit | null => {
    const path = event.composedPath().slice(0, 128);
    const element = path.find(
      (target): target is Element => target instanceof Element
    );
    if (!element) return null;
    const host = path.find(
      (target): target is EditorHost => target instanceof EditorHost
    );
    // Native editors may contain secret controls too. Do not admit those
    // paths even when an EditorHost occurs further up the event chain.
    if (
      path.some(
        target =>
          target instanceof Element &&
          (target.matches(PICK_EXCLUDED_PATH) ||
            (!host && target.matches('[role="textbox"]')))
      )
    )
      return null;
    return { element, host };
  };
  const nativeBlock = (event: MouseEvent) =>
    event
      .composedPath()
      .slice(0, 128)
      .find(
        (target): target is HTMLElement =>
          target instanceof HTMLElement && target.hasAttribute('data-block-id')
      );
  const pickNative = (event: MouseEvent, host: EditorHost) => {
    const kind = isInsideEdgelessEditor(host) ? 'canvas' : 'doc';
    const block = nativeBlock(event);
    const gfx =
      kind === 'canvas' ? host.std.get(GfxControllerIdentifier) : undefined;
    const point = gfx?.viewport.toModelCoordFromClientCoord([
      event.clientX,
      event.clientY,
    ]);
    const model = point
      ? gfx?.getElementByPoint(point[0], point[1])
      : undefined;
    const id =
      kind === 'canvas' ? model?.id : block?.getAttribute('data-block-id');
    if (!id) return false;
    const selection = captureNativeContextSelection({
      host,
      getHost: () => (host.isConnected ? host : undefined),
      scope,
      kind,
      ids: [id],
      assertCurrent: () => {
        assertCurrent();
        if (!root.contains(host)) throw new ChatContextReadError('stale');
      },
      includeDetailedObject: options.includeDetailedObject,
    });
    const tokenKey = JSON.stringify([
      selection.sourceKey,
      selection.sourceGeneration,
    ]);
    let native = nativeSources.get(tokenKey);
    if (!native) {
      native = registerChatPanelPickingSource(
        scope,
        selection.sourceKey,
        selection.sourceGeneration
      );
      nativeSources.set(tokenKey, native);
    }
    if (unpick(native, selection.key)) return true;
    const result = native.pick(selection);
    if (!result.ok) report(result);
    else if (block?.getAttribute('data-block-id') === id)
      markPicked(block, selection.key, native.isSelected);
    return true;
  };
  /** The pill of one semantic element; Content reads it fresh on every send. */
  const uiSelection = (
    captured: NonNullable<ReturnType<typeof captureSemanticDomRef>>
  ): ChatContextSelection => {
    const binding: UiContextBinding = {
      ...scope,
      ownerId: options.ownerId,
      sourceKind: options.sourceKind,
      ownerGeneration: generation,
    };
    const guard = () => {
      assertCurrent();
      if (
        resolveSemanticPickTarget(captured.element, root, scope) !==
        captured.element
      )
        throw new ChatContextReadError('stale');
    };
    return {
      kind: 'ui',
      provenance: 'explicit',
      scope: { ...scope },
      sourceKey,
      sourceGeneration: generation,
      key: uiKey(captured),
      label: captured.label,
      ref: captured.ref,
      binding,
      admittedSequence: ++sequence,
      assertCurrent: guard,
      read: async signal => {
        signal.throwIfAborted();
        guard();
        const projected = projectSemanticPick(captured.element, root, scope);
        if (!projected) throw new ChatContextReadError('unavailable');
        const [picked, ...contained] = projected;
        return {
          refs: [captured.ref],
          elements: [
            {
              ...picked,
              key: captured.ref.id,
              selected: true,
              refs: [captured.ref],
            },
            // What the pick contains comes along, read like the rest of the view.
            ...contained,
          ],
        };
      },
    };
  };
  const uiKey = (captured: { ref: { id: string } }) =>
    JSON.stringify([sourceKey, generation, captured.ref.id]);
  const pickUi = (element: Element) => {
    const captured = captureSemanticDomRef(element, root, scope);
    if (!captured) return false;
    const key = uiKey(captured);
    if (unpick(source, key)) return true;
    const result = source.pick(uiSelection(captured));
    if (!result.ok) report(result);
    else markPicked(captured.element, key, source.isSelected);
    return true;
  };
  // A helper button hands its element over without the selector; a pill
  // already there stays.
  const handUi = (element: Element): HandResult => {
    try {
      assertCurrent();
      const captured = captureSemanticDomRef(element, root, scope);
      if (!captured) return { ok: false, reason: 'unavailable' };
      const key = uiKey(captured);
      if (source.isSelected(key)) return { ok: true };
      const result = source.hand(uiSelection(captured));
      if (result.ok) markPicked(captured.element, key, source.isSelected);
      return result;
    } catch (error) {
      return { ok: false, reason: issueOf(error) };
    }
  };
  handRoots.set(root, handUi);
  // Once an eligible hit belongs to picking, even a denied admission consumes
  // its native action. Errors are reported; they never activate the picked UI.
  const admit = (event: MouseEvent) => {
    if (!userHit(event) || event.button !== 0) return false;
    const hit = pathHit(event);
    if (!hit) return false;
    try {
      assertCurrent();
      const admitted = hit.host
        ? pickNative(event, hit.host)
        : pickUi(hit.element);
      if (admitted) reconcilePicked();
      return admitted;
    } catch (error) {
      report(error);
      return true;
    }
  };
  const stop = (event: Event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  const pointerdown = (event: PointerEvent) => {
    if (admit(event)) {
      downTarget = event.composedPath()[0] ?? event.target;
      stop(event);
    }
  };
  const click = (event: MouseEvent) => {
    const down = downTarget;
    downTarget = null;
    if (
      (control.isEnabled() &&
        down &&
        down === (event.composedPath()[0] ?? event.target)) ||
      admit(event)
    )
      stop(event);
  };
  // Hover marks exactly what a click here would pick. Canvas models have no
  // element of their own, so only DOM and document-block hits are outlined.
  const mouseover = (event: MouseEvent) => {
    const hit = userHit(event) ? pathHit(event) : null;
    setHovered(
      !hit
        ? null
        : hit.host
          ? isInsideEdgelessEditor(hit.host)
            ? null
            : (nativeBlock(event) ?? null)
          : resolveSemanticPickTarget(hit.element, root, scope)
    );
  };
  const mouseleave = () => setHovered(null);
  const listen = (enabled: boolean) => {
    if (enabled === listening) return;
    listening = enabled;
    downTarget = null;
    setHovered(null);
    if (enabled) {
      reconcilePicked();
      root.setAttribute(CONTEXT_PICKING_ROOT_ATTRIBUTE, '');
      root.addEventListener('pointerdown', pointerdown, true);
      root.addEventListener('click', click, true);
      root.addEventListener('mouseover', mouseover, true);
      root.addEventListener('mouseleave', mouseleave);
    } else {
      root.removeAttribute(CONTEXT_PICKING_ROOT_ATTRIBUTE);
      root.removeEventListener('pointerdown', pointerdown, true);
      root.removeEventListener('click', click, true);
      root.removeEventListener('mouseover', mouseover, true);
      root.removeEventListener('mouseleave', mouseleave);
    }
  };
  const unsubscribe = control.subscribe(enabled =>
    listen(enabled && !disposed)
  );
  return () => {
    disposed = true;
    if (handRoots.get(root) === handUi) handRoots.delete(root);
    unsubscribe();
    listen(false);
    reconcilePicked();
    source.dispose();
    for (const registration of nativeSources.values()) registration.dispose();
    nativeSources.clear();
  };
}
